<template>
  <div class="w-full h-full">
    <!-- Right-side form drawer, matching the purchase-order drawer on the perk page. -->
    <Drawer direction="right" :open="showUpdateInvoiceStatusModal" @update:open="(o) => { if (!o) { showUpdateInvoiceStatusModal = false; selectedInvoiceId = undefined } }">
      <DrawerContent class="sm:max-w-md">
        <DrawerHeader class="text-left">
          <DrawerTitle>Update order</DrawerTitle>
          <DrawerDescription>{{ `Update the status of order ${selectedInvoiceId}` }}</DrawerDescription>
        </DrawerHeader>
        <div class="flex flex-col gap-3 overflow-y-auto px-4 flex-1">
          <div class="grid gap-1.5">
            <label class="text-sm font-medium" for="update-invoice-status">Status</label>
            <Select v-model="updateStatus.productInvoiceStatus">
              <SelectTrigger id="update-invoice-status" class="w-full">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="o in statusTypes" :key="o.id" :value="o.id">{{ o.text }}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div v-if="updateStatus.productInvoiceStatus == 'CANCELED'" class="grid gap-1.5">
            <label class="text-sm font-medium" for="update-invoice-cancellation-reason">Cancellation reason</label>
            <Input id="update-invoice-cancellation-reason" v-model="updateStatus.cancellationReason" placeholder="A reason why you are cancelling this order" />
          </div>
          <!-- The request can be rejected (e.g. the order is already fulfilled); previously
               the drawer closed before it resolved and failures were swallowed silently. -->
          <p v-if="updateError" class="text-sm text-destructive">{{ updateError }}</p>
        </div>
        <DrawerFooter class="flex-row justify-end gap-2">
          <Button variant="outline" @click="closeUpdateDrawer()">Cancel</Button>
          <Button :disabled="!updateStatus.productInvoiceStatus || updating" @click="updateInvoice()">
            {{ updating ? 'Updating…' : 'Update' }}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>

    <Card>
      <CardHeader>
        <CardTitle>Perks orders</CardTitle>
        <CardDescription>View your perks orders</CardDescription>
      </CardHeader>
      <CardContent>
        <DataTable
          :headers="headersTable1"
          :items="invoices"
          :loading="loading"
          :error="error"
          @retry="getInvoices(serverOptions.page - 1, serverOptions.rowsPerPage)"
          v-model:server-options="serverOptions"
          :server-items-length="serverItemsLength"
          searchable
          search-placeholder="Search by perk ID or name"
          :search="productSearchInput"
          @update:search="productSearchInput = $event"
          @search="getInvoices(0, serverOptions.rowsPerPage)"
          @clear-search="onClearSearch()"
          :filter-groups="invoiceFilterGroups"
          :filters="invoiceFilters"
          @filter-change="onFilterChange"
        >
          <!-- Perk name links through to the perk it was ordered against. -->
          <template #item-productName="{ productId, productName }">
            <router-link
              v-if="productId"
              :to="`/perks-and-rewards/product-catalogue/${productId}`"
              class="text-sm font-medium text-primary underline-offset-2 hover:underline"
              @click.stop
            >{{ productName }}</router-link>
            <span v-else class="text-sm font-medium">{{ productName || '—' }}</span>
          </template>

          <template #item-id="{ id }">
            <CopyId :value="id" :max-width="120" />
          </template>

          <template #item-accountingInvoiceId="{ accountingInvoiceId }">
            <CopyId v-if="accountingInvoiceId" :value="accountingInvoiceId" :max-width="120" />
            <span v-else class="text-muted-foreground">—</span>
          </template>

          <template #item-productInvoiceStatus="{ productInvoiceStatus }">
            <Badge :variant="statusVariant(productInvoiceStatus)" class="whitespace-nowrap">{{ productInvoiceStatus }}</Badge>
          </template>

          <!-- Price Formatting Slots -->
          <template #item-publisherPerUnitPrice="{ publisherPerUnitPrice }">
            <span class="text-sm tabular-nums whitespace-nowrap">£{{ Number(publisherPerUnitPrice || 0).toFixed(2) }}</span>
          </template>

          <template #item-purchasablePerUnitPrice="{ purchasablePerUnitPrice }">
            <span class="text-sm tabular-nums whitespace-nowrap">£{{ Number(purchasablePerUnitPrice || 0).toFixed(2) }}</span>
          </template>

          <template #item-expectedInvoiceAmountFromSupplier="{ expectedInvoiceAmountFromSupplier }">
            <span class="text-sm tabular-nums whitespace-nowrap">£{{ Number(expectedInvoiceAmountFromSupplier || 0).toFixed(2) }}</span>
          </template>

          <template #item-boogiRevenue="{ boogiRevenue }">
            <span class="text-sm tabular-nums whitespace-nowrap">£{{ Number(boogiRevenue || 0).toFixed(2) }}</span>
          </template>

          <template #item-unitValue="{ unitValue }">
            <span class="text-sm tabular-nums whitespace-nowrap">£{{ Number(unitValue || 0).toFixed(2) }}</span>
          </template>

          <template #item-invoicedAmountToPublisher="{ invoicedAmountToPublisher }">
            <span class="text-sm tabular-nums whitespace-nowrap">£{{ Number(invoicedAmountToPublisher || 0).toFixed(2) }}</span>
          </template>

          <!-- <template #item-offerRequested="retailers">
                        <div class="flex justify-start items-center gap-1">
                            {{retailers.programOffers.length}}/{{retailers.offersList.length }}
                        </div>
                    </template>
                    <template #item-offerApproved="retailers">
                        <div class="flex justify-start items-center gap-1">
                           {{retailers.programOffers.filter(offer => offer.offerApproval == "APPROVED").length }}/ {{retailers.offersList.length }}
                        </div>
                    </template> -->

          <!-- Only a SUBMITTED order can still change status; showing the action on
               fulfilled/cancelled rows offered something that cannot be done. -->
          <template #item-action="retailer">
            <div class="flex justify-end items-center gap-1">
              <Button
                v-if="retailer.productInvoiceStatus === 'SUBMITTED'"
                size="sm"
                @click="() => { showUpdateInvoiceStatusModal = true; selectedInvoiceId = retailer.id }"
              >Update status</Button>
            </div>
          </template>
        </DataTable>
      </CardContent>
    </Card>
  </div>
</template>

<script setup></script>

<script>
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent,
  Button, Input, Badge, CopyId,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter,
} from '@boogi/ui'
import { DataTable, toast } from '@boogi/ui'

export default {
  name: 'manage-publsihers',
  components: {
    Card, CardHeader, CardTitle, CardDescription, CardContent,
    Button, Input, Badge, CopyId,
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
    Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter,
    DataTable,
  },
  props: {},

  data() {
    return {
      serverOptions: {
        page: 1,
        rowsPerPage: 25,
      },

      showUpdateInvoiceStatusModal: false,
      updateError: '',
      updating: false,
      selectedInvoiceId: undefined,

      statusTypes: [{ id: 'CANCELED', text: 'CANCELED' }],

      updateStatus: {
        productInvoiceStatus: 'CANCELED',
        cancellationReason: undefined,
        accountingInvoiceId: undefined,
      },

      serverItemsLength: undefined,
      downloading: false,

      showErrorModal: false,
      showSuccessModal: false,
      showFormModal: false,
      loading: true,
      error: false,
      productSearchInput: '',
      productTypes: [
        { id: 'VOUCHER', text: 'Voucher' },
        { id: 'GIFTCARD', text: 'Giftcard' },
      ],
      categories: [],
      countryOptions: [],
      selectedType: [],
      selectedCategory: [],
      selectedCountry: [],
      headersTable1: [
        { text: 'Perk', value: 'productName' },
        { text: 'Order Id', value: 'id' },
        { text: 'Accounting Id', value: 'accountingInvoiceId' },
        { text: 'Status', value: 'productInvoiceStatus' },
        { text: 'Voucher value', value: 'unitValue' },
        { text: 'Quantity', value: 'quantity' },
        { text: 'Unit price', value: 'publisherPerUnitPrice' },
        { text: 'Invoice value', value: 'invoicedAmountToPublisher' },
        { text: 'Action', value: 'action' },
      ],
      invoices: [],
    }
  },
  created() {
    this.getCategories()
    this.getCountries()
    this.getInvoices(0, 25)
  },
  methods: {
    onClearSearch() {
      this.productSearchInput = ''
      this.serverOptions = { page: 1, rowsPerPage: this.serverOptions.rowsPerPage || 25 }
      this.getInvoices(0, this.serverOptions.rowsPerPage)
    },

    onFilterChange(updated) {
      const typeIds = (updated && updated.productType) || []
      this.selectedType = this.productTypes.filter((o) => typeIds.includes(o.id))
      this.selectedCategory = (updated && updated.productCategory) || []
      this.selectedCountry = (updated && updated.country) || []
      this.serverOptions = { page: 1, rowsPerPage: this.serverOptions.rowsPerPage || 25 }
      this.getInvoices(0, this.serverOptions.rowsPerPage)
    },

    async getCategories() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/product-catalogue/categories`,
          { method: 'GET', headers: { 'Content-Type': 'application/json', Authorization: `${token}` } }
        )
        if (response.ok) this.categories = await response.json()
      } catch (error) {
        return
      }
    },

    async getCountries() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/product-catalogue/countries`,
          { method: 'GET', headers: { 'Content-Type': 'application/json', Authorization: `${token}` } }
        )
        if (response.ok) this.countryOptions = await response.json()
      } catch (error) {
        return
      }
    },

    statusVariant(status) {
      const s = String(status || '').toUpperCase()
      if (['INVOICE_PAID', 'PAID', 'COMPLETED', 'FULFILLED', 'INVOICE_CREATED'].includes(s)) return 'success'
      if (['CANCELED', 'CANCELLED', 'FAILED', 'DECLINED', 'REJECTED'].includes(s)) return 'destructive'
      if (['PENDING', 'PROCESSING', 'IN_PROGRESS', 'DRAFT'].includes(s)) return 'warning'
      return 'secondary'
    },
    closeUpdateDrawer() {
      this.showUpdateInvoiceStatusModal = false
      this.selectedInvoiceId = undefined
      this.updateError = ''
    },

    async updateInvoice() {
      if (this.updating) return
      this.updating = true
      this.updateError = ''
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/product-invoice/${this.selectedInvoiceId}`,
          {
            method: 'POST',
            body: JSON.stringify(this.updateStatus),
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          this.getInvoices(0, 25)
          this.updateStatus = {
            productInvoiceStatus: 'INVOICE_CREATED',
            cancellationReason: undefined,
            accountingInvoiceId: undefined,
          }
          this.closeUpdateDrawer()
          toast.success('Order updated')
        } else {
          // Surface the server's reason (e.g. "Order can no longer be cancelled") instead
          // of closing the drawer and leaving the row looking unchanged for no reason.
          const text = await response.text().catch(() => '')
          this.updateError = text || 'Could not update this order. Please try again.'
        }
      } catch (error) {
        this.updateError = 'Could not reach the server. Please try again.'
      } finally {
        this.updating = false
      }
    },

    async getInvoices(page, size) {
      this.loading = true
      this.error = false
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        const params = new URLSearchParams()
        params.append('page', page)
        params.append('size', size)
        if (this.productSearchInput) params.append('search', this.productSearchInput)
        if (this.selectedType.length > 0) {
          params.append('type', this.selectedType.map((t) => t.id).join(','))
        }
        if (this.selectedCategory.length > 0) {
          params.append('category', this.selectedCategory.join(','))
        }
        if (this.selectedCountry.length > 0) {
          params.append('country', this.selectedCountry.join(','))
        }

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/product-invoice?${params.toString()}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()

          this.serverItemsLength = object.totalElements
          this.invoices = object.content
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },
  },
  computed: {
    invoiceFilterGroups() {
      const groups = [
        {
          key: 'productType',
          label: 'Type',
          options: this.productTypes.map((o) => ({ value: o.id, label: o.text })),
        },
      ]
      if (this.categories.length > 0) {
        groups.push({
          key: 'productCategory',
          label: 'Category',
          options: this.categories.map((c) => ({ value: c, label: c })),
        })
      }
      if (this.countryOptions.length > 0) {
        groups.push({
          key: 'country',
          label: 'Country',
          options: this.countryOptions.map((c) => ({ value: c, label: c })),
        })
      }
      return groups
    },
    invoiceFilters() {
      return {
        productType: this.selectedType.map((o) => o.id),
        productCategory: this.selectedCategory,
        country: this.selectedCountry,
      }
    },
  },
  watch: {
    serverOptions: {
      deep: true,
      handler(val) {
        this.getInvoices(val.page - 1, val.rowsPerPage)
      },
    },
  },
}
</script>
